import { useEffect } from 'react';
import { useEditorStore } from '../store/editorStore';
import { usePlayheadStore } from '../store/playheadStore';
import { useLoopStore } from '../store/loopStore';
import { getAudioEngine } from '../renderer/rendererBridge';

export function useKeyboardShortcuts() {
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      // Don't fire shortcuts when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const mod = e.metaKey || e.ctrlKey;

      // Space: play/pause
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        const s = usePlayheadStore.getState();
        if (s.playing) {
          s.setPlaying(false);
          getAudioEngine()?.stop();
        } else {
          const loop = useLoopStore.getState();
          if (loop.selectionStart !== null && loop.selectionEnd !== null) {
            s.setCurrentTime(loop.selectionStart);
          }
          s.setPlaying(true);
          const audio = getAudioEngine();
          if (audio) {
            const tracks = useEditorStore.getState().project.tracks;
            const timeMs = usePlayheadStore.getState().currentTimeMs;
            let sourceMs = -1;
            let assetId: string | undefined;
            for (const track of tracks) {
              if (track.type !== 'audio') continue;
              for (const clip of track.clips) {
                if (timeMs >= clip.startMs && timeMs < clip.startMs + clip.durationMs) {
                  sourceMs = clip.sourceOffsetMs + (timeMs - clip.startMs);
                  assetId = clip.assetId;
                  break;
                }
              }
              if (sourceMs >= 0) break;
            }
            if (sourceMs >= 0) audio.play(sourceMs, assetId);
          }
        }
        return;
      }

      // Undo: Cmd/Ctrl+Z
      if (mod && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        useEditorStore.getState().undo();
        return;
      }

      // Redo: Cmd/Ctrl+Shift+Z
      if (mod && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        useEditorStore.getState().redo();
        return;
      }

      // Split: Cmd/Ctrl+B
      if (mod && e.key === 'b') {
        e.preventDefault();
        const { selectedClipId } = useEditorStore.getState().timeline;
        if (!selectedClipId) return;
        const currentTimeMs = usePlayheadStore.getState().currentTimeMs;
        useEditorStore.getState().splitClip(selectedClipId, currentTimeMs);
        return;
      }

      // Zoom in: Cmd/Ctrl+=
      if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        useEditorStore.getState().zoomIn();
        return;
      }

      // Zoom out: Cmd/Ctrl+-
      if (mod && e.key === '-') {
        e.preventDefault();
        useEditorStore.getState().zoomOut();
        return;
      }

      // Zoom to fit: Cmd/Ctrl+0
      if (mod && e.key === '0') {
        e.preventDefault();
        const canvas = document.querySelector('.w-full.h-full canvas, canvas[class*="w-full"]');
        if (canvas) {
          useEditorStore.getState().zoomToFit((canvas as HTMLCanvasElement).getBoundingClientRect().width);
        }
        return;
      }

      // Loop: I = set in point, O = set out point, L = toggle loop
      if (!mod && e.key === 'i') {
        e.preventDefault();
        const ms = usePlayheadStore.getState().currentTimeMs;
        const { selectionEnd } = useLoopStore.getState();
        useLoopStore.getState().setSelection(ms, selectionEnd ?? ms);
        return;
      }

      if (!mod && e.key === 'o') {
        e.preventDefault();
        const ms = usePlayheadStore.getState().currentTimeMs;
        const { selectionStart } = useLoopStore.getState();
        useLoopStore.getState().setSelection(selectionStart ?? 0, ms);
        return;
      }

      if (!mod && e.key === 'l') {
        e.preventDefault();
        useLoopStore.getState().toggleLoop();
        return;
      }

      // Home = scroll to start, End = scroll to end
      if (e.key === 'Home') {
        e.preventDefault();
        useEditorStore.getState().setScrollOffset(0);
        usePlayheadStore.getState().setCurrentTime(0);
        return;
      }

      if (e.key === 'End') {
        e.preventDefault();
        const duration = useEditorStore.getState().project.durationMs;
        usePlayheadStore.getState().setCurrentTime(duration);
        return;
      }

      // Delete selected clip: Delete or Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const { selectedClipId } = useEditorStore.getState().timeline;
        if (!selectedClipId) return;
        useEditorStore.getState().removeClip(selectedClipId);
        useEditorStore.getState().selectClip(null);
        return;
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}
